import { PhoneNumber, PhoneNumberType, PhoneNumberUtil } from 'google-libphonenumber';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { optionalOnInput } from '@Common/types/zod/zod-optional-input';

export const createZodPostVerifyOtpRequest = (type: 'otp' | 'backup_code') =>
  z.object({
    code: z
      .string({
        message: 'login_page.messages.form.errors.required',
      })
      .trim()
      .length(type === 'otp' ? 6 : 10, {
        message: 'login_page.messages.form.errors.invalid_length',
      }),
  });

export const ZodPostVerifyPhoneNumberRequest = z.object({
  phoneNumber: optionalOnInput(
    z
      .string({
        message: 'login_page.messages.form.errors.required',
      })
      .superRefine((data, ctx) => {
        try {
          const validator = PhoneNumberUtil.getInstance();
          let parsed: PhoneNumber;
          try {
            parsed = validator.parseAndKeepRawInput(data, 'US');
          } catch {
            parsed = validator.parseAndKeepRawInput(data, 'BE');
          }

          if (validator.isValidNumber(parsed)) {
            const numberType = validator.getNumberType(parsed);
            if (
              numberType !== PhoneNumberType.MOBILE &&
              numberType !== PhoneNumberType.FIXED_LINE_OR_MOBILE
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'resources.bo_common.validation.invalid_mobile_phone_number',
              });
            }
            return;
          }

          const result = validator.isPossibleNumberWithReason(parsed);
          match(result)
            .with(PhoneNumberUtil.ValidationResult.TOO_SHORT, () => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'login_page.messages.form.errors.phoneNumber_tooShort',
              });
            })
            .with(PhoneNumberUtil.ValidationResult.TOO_LONG, () => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'login_page.messages.form.errors.phoneNumber_tooLong',
              });
            })
            .with(PhoneNumberUtil.ValidationResult.INVALID_COUNTRY_CODE, () => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'login_page.messages.form.errors.phoneNumber_invalidCountryCode',
              });
            })
            .otherwise(() => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'login_page.messages.form.errors.phoneNumber_format',
              });
            });
        } catch {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'login_page.messages.form.errors.phoneNumber_format',
          });
        }
      }),
  ),
});

export const ZodPostVerifyPhoneNumberWithCodeRequest = z.object({
  ...ZodPostVerifyPhoneNumberRequest.shape,
  code: z
    .string()
    .nonempty({
      message: 'login_page.messages.form.errors.required',
    })
    .trim(),
});

export const ZodPostForgotPasswordRequest = z.object({
  email: z
    .string()
    .nonempty({
      message: 'login_page.messages.form.errors.required',
    })
    .trim()
    .email({ message: 'login_page.messages.form.errors.email_format' }),
});

export type PostForgotPasswordRequest = z.TypeOf<typeof ZodPostForgotPasswordRequest>;

export const ZodPostFirstConnectionRequest = z.object({
  email: z
    .string()
    .nonempty({
      message: 'login_page.messages.form.errors.required',
    })
    .trim()
    .email({ message: 'login_page.messages.form.errors.email_format' }),
  code: z
    .string()
    .nonempty({
      message: 'login_page.messages.form.errors.required',
    })
    .trim(),
});

export type PostFirstConnectionRequest = z.TypeOf<typeof ZodPostFirstConnectionRequest>;

export const ZodPostFirstConnectionRequestWithPhoneNumber = z.object({
  email: z
    .string()
    .nonempty({
      message: 'login_page.messages.form.errors.required',
    })
    .trim()
    .email({ message: 'login_page.messages.form.errors.email_format' }),
  code: z
    .string()
    .nonempty({
      message: 'login_page.messages.form.errors.required',
    })
    .trim()
    .nullish(),
});

export type PostFirstConnectionRequestWithPhoneNumber = z.TypeOf<
  typeof ZodPostFirstConnectionRequestWithPhoneNumber
>;

export const ZodPostChangePasswordRequest = z
  .object({
    password: z
      .string({
        message: 'login_page.messages.form.errors.required',
      })
      .min(12, { message: 'login_page.messages.form.errors.password_length_min' })
      .max(128, { message: 'login_page.messages.form.errors.password_length_max' }),
    confirm_password: z.string({
      message: 'login_page.messages.form.errors.required',
    }),
  })
  .superRefine((data, ctx) => {
    let uppercase = false;
    let lowercase = false;
    let numeric = false;
    let symbol = false;
    const { password } = data;
    for (let i = 0; i < password.length; i++) {
      const char = password.charCodeAt(i);
      if (char >= 65 && char <= 90) {
        uppercase = true;
      } else if (char >= 97 && char <= 122) {
        lowercase = true;
      } else if (char >= 48 && char <= 57) {
        numeric = true;
      } else if (char < 33 || char > 126) {
        continue;
      } else {
        symbol = true;
      }
    }

    if (!uppercase) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['password'],
        message: 'login_page.messages.form.errors.password_req_uppercase',
      });
    }

    if (!lowercase) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['password'],
        message: 'login_page.messages.form.errors.password_req_lowercase',
      });
    }

    if (!numeric) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['password'],
        message: 'login_page.messages.form.errors.password_req_number',
      });
    }

    if (!symbol) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['password'],
        message: 'login_page.messages.form.errors.password_req_symbol',
      });
    }
  })
  .refine((data) => data.password === data.confirm_password, {
    path: ['confirm_password'],
    message: 'login_page.messages.form.errors.password_mismatch',
  });

export type PostChangePasswordRequest = z.TypeOf<typeof ZodPostChangePasswordRequest>;

export const ZodPostRequestEmailRequest = z.object({
  email: z
    .string({
      message: 'login_page.messages.form.errors.required',
    })
    .trim()
    .email({ message: 'login_page.messages.form.errors.email_format' }),
  password: z
    .string({
      message: 'login_page.messages.form.errors.required',
    })
    .min(12, { message: 'login_page.messages.form.errors.password_length_min' })
    .max(128, { message: 'login_page.messages.form.errors.password_length_max' }),
  rememberMe: z.boolean(),
});

export type PostRequestEmailRequest = z.TypeOf<typeof ZodPostRequestEmailRequest>;
